import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import {
  NextButtonContainer,
  SectionContent,
  SectionIconWrapper,
  WrapperVerticalPadding,
  sidePadding,
} from "Payments/PaymentPage/PaymentFormScreen/styles";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import ErrorMessage from "Payments/PaymentPage/Shared/ErrorMessage";
import { useGetPaymentTypeVerbiage } from "Payments/PaymentPage/Shared/useGetPaymentTypeVerbiage";
import { AnimatePresence, motion } from "framer-motion";
import qs from "qs";

import FnrButton from "UI/FnrButton3";
import FnrIcon from "UI/FnrIcon";
import { useResizingChildContext } from "UI/SharedResizingArea";
import { colors } from "UI/colors";

import { usePaymentForm } from "../PaymentFormContext";

import AmountInput from "./AmountInput";
import AmountSuggestions from "./AmountSuggestions";
import RecurringPaymentSection from "./RecurringSettings/RecurringPaymentSection";
import { useSetAmountToComplete } from "./useSetAmountToComplete";

export default function AmountSection({ amountURLParam }) {
  const location = useLocation();
  const {
    state: {
      amount,
      paymentMethods,
      user,
      isMobile,
      fund: { fundColorSafe, paymentPageSettings },
      recurringPayment: { startDate, recurring },
    },
    dispatch: paymentPageDispatch,
  } = usePaymentPage();
  const {
    state: { formStages, amountErrorMsg },
    dispatch: formDispatch,
  } = usePaymentForm();
  const { isResizing, hasAnimatedIn } = useResizingChildContext();
  const setAmountToComplete = useSetAmountToComplete();
  const isExpanded = formStages.amount === "expanded";
  const { verb, noun } = useGetPaymentTypeVerbiage();
  const inputRef = useRef();
  const [suggestionButtonAmount, setSuggestionButtonAmount] = useState(null);

  const { signupListID, isSuggestedDonation } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const amountIsNotEditable = signupListID && !isSuggestedDonation;

  const setErrorMsg = useCallback(
    (message) => {
      formDispatch({
        type: "setAmountErrorMsg",
        payload: message,
      });
    },
    [formDispatch]
  );

  useEffect(() => {
    if (!isResizing && hasAnimatedIn && inputRef.current)
      inputRef.current.focus();
  }, [isResizing, hasAnimatedIn]);

  useEffect(() => {
    if (
      (amount && Number(amount) >= 2) ||
      (suggestionButtonAmount && Number(suggestionButtonAmount) >= 2)
    )
      setErrorMsg(null);
  }, [amount, suggestionButtonAmount, setErrorMsg]);

  const showAmountSuggestions =
    isExpanded &&
    paymentPageSettings?.suggestedAmounts?.filter((x) => x > 0).length > 1 &&
    !amountURLParam;

  const isHappyPath = user.loginLevel === "full" && paymentMethods?.length > 0;
  const showNextButton = isExpanded && !isHappyPath;

  const runValidations = () => {
    if (!amount) {
      setErrorMsg("Please enter an amount");
      return false;
    } else if (amount && Number(amount) < 2) {
      setErrorMsg("Please enter an amount greater than $2");
      return false;
    } else if (Number(amount) > 100000) {
      setErrorMsg("The maximum amount is $100,000");
      return false;
    }

    return true;
  };

  const handleNextClick = () => {
    const isValid = runValidations();

    if (isValid) {
      setAmountToComplete();
      paymentPageDispatch({
        type: "setEditingRecurringPayment",
        payload: false,
      });
    } else {
      inputRef.current.focus();
    }
  };

  return (
    <Wrapper>
      <TopPadding />
      <AmountSectionContent>
        <IconWrapper
          initial={{ width: isMobile && isExpanded ? 0 : 80 }}
          animate={{ width: isMobile && isExpanded ? 0 : 80 }}
        >
          <IconInside>
            <SectionIconWrapper style={{ backgroundColor: colors.fnGreen }}>
              <FnrIcon fas fa-dollar-sign />
            </SectionIconWrapper>
          </IconInside>
        </IconWrapper>
        <ContentInside>
          {/* <AnimatePresence>
            {isExpanded && (
              <LeadTextAround exit={{ height: 0, opacity: 0 }}>
                <LeadText style={{ paddingBottom: 10 }}>
                  How much would you
                  <br />
                  like to {verb}?
                </LeadText>
              </LeadTextAround>
            )}
          </AnimatePresence> */}
          <AmountInputContainer
            isExpanded={isExpanded}
            hasErrorMessage={amountErrorMsg}
            amountIsNotEditable={amountIsNotEditable}
          >
            <AmountInput
              ref={inputRef}
              isExpanded={isExpanded}
              handleNextClick={handleNextClick}
              suggestionButtonAmount={suggestionButtonAmount}
              setSuggestionButtonAmount={setSuggestionButtonAmount}
              runValidations={runValidations}
              showNextButton={showNextButton}
            />
            <AnimatePresence>
              {showAmountSuggestions && (
                <AmountExtras
                  key="extras"
                  exit={{
                    opacity: 0,
                    height: 0,
                    scale: 0.8,
                    transition: { type: "tween" },
                  }}
                >
                  <div style={{ height: 3 }} />
                  <AmountSuggestions
                    setSuggestionButtonAmount={setSuggestionButtonAmount}
                  />
                </AmountExtras>
              )}
            </AnimatePresence>
          </AmountInputContainer>

          <ErrorMessageContainer isExpanded={isExpanded}>
            <ErrorMessage message={amountErrorMsg} />
          </ErrorMessageContainer>

          <RecurringPaymentSection />

          <NextButtonContainer show={showNextButton}>
            <FnrButton
              data-cy="amountInputNextButton"
              color={fundColorSafe}
              onClick={handleNextClick}
              fullWidth
              size="large"
              disabled={suggestionButtonAmount}
            >
              Next
            </FnrButton>
          </NextButtonContainer>
        </ContentInside>
      </AmountSectionContent>

      <WrapperVerticalPadding />
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  ${sidePadding}
  overflow: hidden;
`;

const TopPadding = styled.div`
  height: 10px;
`;

const AmountSectionContent = styled(SectionContent)`
  padding: 8px;
`;
const IconWrapper = styled(motion.div)`
  flex-grow: 0;
  flex-shrink: 0;
  align-self: flex-start;
  display: flex;
  justify-content: flex-end;
`;
const IconInside = styled(motion.div)`
  padding-left: 6px;
  padding-top: 6px;
`;

const AmountExtras = styled(motion.div)`
  height: auto;
`;
const ContentInside = styled.div`
  position: relative;
  flex-grow: 1;
  overflow: hidden;
`;
const LeadTextAround = styled(motion.div)`
  height: auto;
  overflow: hidden;
`;

const AmountInputContainer = styled.div`
  position: relative;
  padding: 4px;
  border: 2px solid
    ${({ isExpanded, hasErrorMessage }) =>
      isExpanded || hasErrorMessage ? "#bec3c7" : "rgba(190, 195, 199,0)"};
  border-radius: 9px;
  transition: border 0.6s;
  overflow: hidden;

  &:hover {
    transition: border 0.2s;
    border: 2px solid
      ${({ isExpanded }) => (isExpanded ? "#278dc1" : "#bec3c7")};
    border: ${({ amountIsNotEditable }) =>
      amountIsNotEditable ? "2px solid transparent" : null};
  }
  &:focus-within {
    transition: border 0.2s;
    border: 2px solid #278dc1;
  }
`;

const ErrorMessageContainer = styled.div`
  @media screen and (max-width: 480px) {
    margin-left: ${({ isExpanded }) => (isExpanded ? "auto" : "-80px")};
  }
`;

const Message = styled.div`
  color: ${colors.fnRedNotice};
  margin: 6px 0;
  text-align: center;
  position: relative;
`;
